<template>
    <div class="flex h-screen bg-gray-50">
     <SideBar class="flex-shrink-0" />
     <div class="flex-1 overflow-auto p-4 md:p-6">
           <div class="min-h-screen bg-gray-50 py-8 px-4">
           <div class="max-w-6xl mx-auto flex flex-col">
               <div :class="[isDirecttide ? 'order-2 mt-8' : 'order-1']" class="mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div>
                        <h2 class="text-2xl font-bold text-gray-900">Current Inventory</h2>
                        <p class="text-sm text-gray-500 mt-1">Manage and track your products inventory</p>
                    </div>
                    <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <button
                          @click="openAddProductModal"
                          class="px-4 py-2.5 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold rounded-lg transition-colors whitespace-nowrap text-sm flex items-center justify-center gap-2 w-full sm:w-auto shadow-sm"
                        >
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                          </svg>
                          Add Product
                        </button>
                        <div class="relative w-full sm:w-64">
                            <input
                             v-model="inventorySearchTerm"
                             type="text"
                             placeholder="Search SKU or Name..."
                             class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm"
                            />
                            <div class="absolute left-3 top-3 text-gray-400">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
       
                <div
                  :class="[
                    isDirecttide ? 'md:hidden' : ''
                  ]"
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                >
                    <div
                    v-for="item in filteredInventorySummary"
                    :key="item.sku"
                    @click="openAdjustmentHistory(item.sku)"
                    class="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                    >
                    <div class="flex items-start justify-between mb-2">
                        <div>
                        <div class="text-xs font-medium text-gray-500 uppercase">{{ item.sku }}</div>
                        <div class="text-sm font-medium text-gray-900 mt-1">{{ item.name }}</div>
                        </div>
                        <span
                        :class="[
                            'px-2 py-1 text-xs font-semibold rounded-full',
                            item.totalQty > 0 ? 'bg-green-100 text-green-800' : item.totalQty < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        ]"
                        >
                        {{ item.totalQty > 0 ? '+' : '' }}{{ item.totalQty }}
                        </span>
                    </div>
                    <div class="text-xs text-gray-500 mt-2 flex justify-between items-center">
                        <div class="text-blue-600 font-medium">{{ item.adjustmentCount }} adjustments</div>
                        <button 
                          @click.stop="openEditProductModal(item)"
                          class="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                        >
                          Edit Product
                        </button>
                    </div>
                    </div>
                </div>

                <!-- Table View for Directtide Users on Desktop -->
                <div v-if="isDirecttide" class="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <table class="w-full">
                    <thead class="bg-gray-50">
                      <tr class="border-b border-gray-200">
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">SKU</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Product Name</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Price</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantity</th>
                        <th class="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Adjustments</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr 
                        v-for="item in filteredInventorySummary" 
                        :key="item.sku"
                        class="hover:bg-gray-50 transition-colors"
                      >
                        <td class="px-6 py-4 text-sm font-semibold text-gray-900">{{ item.sku }}</td>
                        <td class="px-6 py-4 text-sm text-gray-700">{{ item.name }}</td>
                        <td class="px-6 py-4 text-sm text-right font-medium text-gray-900">${{ Number(item.price || 0).toFixed(2) }}</td>
                        <td class="px-6 py-4 text-sm text-right">
                          <span 
                            :class="[
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                              item.totalQty > 0 ? 'bg-green-100 text-green-800' : item.totalQty < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                            ]"
                          >
                            {{ item.totalQty > 0 ? '+' : '' }}{{ item.totalQty }}
                          </span>
                        </td>
                        <td 
                          @click="openAdjustmentHistory(item.sku)"
                          class="px-6 py-4 text-sm text-center text-blue-600 hover:text-blue-800 font-medium cursor-pointer hover:underline"
                        >
                          {{ item.adjustmentCount }} adjustments
                        </td>
                        <td class="px-6 py-4 text-sm">
                          <div class="flex items-center gap-2">
                            <button 
                              @click="openAdjustmentHistory(item.sku)"
                              class="px-3 py-1.5 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold text-xs rounded transition-colors whitespace-nowrap"
                            >
                              View History
                            </button>
                            <button 
                              @click="openEditProductModal(item)"
                              class="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold text-xs rounded transition-colors whitespace-nowrap"
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
               </div>

               <div :class="[isDirecttide ? 'order-1 mb-8' : 'order-2']" class="flex flex-col">
                  <div class="mb-4">
                  <button
                      @click="showLedger = !showLedger"
                      class="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
                  >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {{ showLedger ? 'Hide' : 'Show' }} Adjustment Ledger
                  </button>
                  </div>
       
                  <div v-show="showLedger" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 flex-wrap gap-3">
                        <div>
                            <h3 class="text-lg font-semibold text-gray-800">Adjustment Ledger</h3>
                            <p class="text-xs text-gray-600 mt-0.5">Filter and view adjustment history log</p>
                        </div>
                        <button v-if="[1,14,28,29].includes(store.getters['auth/currentUser']?.id)"
                            @click="openAddModal"
                            class="px-4 py-2 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold rounded-lg transition-colors whitespace-nowrap text-sm flex items-center gap-2"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Entry
                        </button>
                    </div>
                    <div class="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3 bg-white">
                        <div class="flex-1 relative">
                            <input
                            v-model="searchTerm"
                            type="text"
                            placeholder="Search SKU or Name..."
                            class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                            />
                            <div class="absolute left-3 top-3.5 text-gray-400">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                        <select
                        v-model="reasonFilter"
                        class="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                        >
                        <option value="">All Reasons</option>
                        <option value="transfer_in">Transfer In</option>
                        <option value="transfer_out">Transfer Out</option>
                        <option value="adjustment">Adjustment</option>
                        </select>
                    </div>
                  <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-3 text-sm">
                      <div class="flex items-center gap-2">
                      <span class="text-gray-600">Items shown:</span>
                      <span class="font-semibold text-gray-900">{{ ledger.length }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                      <span class="text-gray-600">Total Qty Δ:</span>
                      <span
                          :class="[
                          'font-semibold',
                          qtyDeltaTotal > 0 ? 'text-green-600' : qtyDeltaTotal < 0 ? 'text-red-600' : 'text-gray-900'
                          ]"
                      >
                      {{ qtyDeltaTotal > 0 ? '+' : '' }}{{ qtyDeltaTotal }}
                  </span>
                      </div>
                  </div>
                  <div class="hidden md:block overflow-x-auto">
                      <table class="w-full">
                      <thead class="bg-gray-50 sticky top-0 z-10">
                          <tr class="border-b border-gray-200">
                          <th
                              v-for="col in columns"
                              :key="col.key"
                              @click="sortBy(col.key)"
                              class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors group"
                          >
                              <div class="flex items-center gap-2">
                              <span>{{ col.label }}</span>
                              <div class="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg
                                  :class="[
                                      'w-3 h-3 -mb-1',
                                      sortKey === col.key && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-400'
                                  ]"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  >
                                  <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 01-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" />
                                  </svg>
                                  <svg
                                  :class="[
                                      'w-3 h-3',
                                      sortKey === col.key && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-400'
                                  ]"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  >
                                  <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" />
                                  </svg>
                              </div>
                              </div>
                          </th>
                          </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200">
                          <tr
                          v-for="(row, idx) in ledger"
                          :key="row.id"
                          :class="[
                              'hover:bg-gray-50 transition-colors',
                              idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          ]"
                          >
                          <td class="px-4 py-3 text-sm text-gray-900 font-medium">{{ row.sku }}</td>
                          <td class="px-4 py-3 text-sm text-gray-700">{{ row.name }}</td>
                          <td class="px-4 py-3 text-sm">
                              <span
                              :class="[
                                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                                  row.qtyDelta > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              ]"
                              >
                              {{ row.qtyDelta > 0 ? '+' : '' }}{{ row.qtyDelta }}
                              </span>
                          </td>
                          <td class="px-4 py-3 text-sm">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {{ row.reason.replace('_', ' ') }}
                              </span>
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-600 font-mono">{{ row.referenceId }}</td>
                          <td class="px-4 py-3 text-sm text-gray-600">
                           <div
                               class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                               :style="{ backgroundColor: getAvatarColor(row.user_id) }"
                               >
                               {{ getInitials(getUserById(row.user_id).name) }}
                           </div>
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(row.createdAt) }}</td>
                          </tr>
                      </tbody>
                      </table>
                  </div>
                  <div class="md:hidden divide-y divide-gray-200">
                      <div
                      v-for="row in ledger"
                      :key="row.id"
                      class="p-4 hover:bg-gray-50 transition-colors"
                      >
                      <div class="space-y-2 text-sm">
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">SKU:</span>
                          <span class="text-gray-900 font-semibold">{{ row.sku }}</span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">Name:</span>
                          <span class="text-gray-900">{{ row.name }}</span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">Qty Δ:</span>
                          <span
                              :class="[
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                              row.qtyDelta > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              ]"
                          >
                              {{ row.qtyDelta > 0 ? '+' : '' }}{{ row.qtyDelta }}
                          </span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">Reason:</span>
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {{ row.reason.replace('_', ' ') }}
                          </span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">Reference ID:</span>
                          <span class="text-gray-900 font-mono text-xs">{{ row.referenceId }}</span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">User:</span>
                          <span class="text-gray-900">{{ getUserById(row.user_id).name}}</span>
                          </div>
                          <div class="flex justify-between">
                          <span class="text-gray-600 font-medium">Created At:</span>
                          <span class="text-gray-500 text-xs">{{ formatDate(row.createdAt) }}</span>
                          </div>
                      </div>
                      </div>
                  </div>
                  <div class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                      <div class="text-sm text-gray-600">
                          Showing {{ (page - 1) * pageSize + 1 }}
                          to {{ Math.min(page * pageSize, totalRows) }}
                          of {{ totalRows }} entries                    
                          </div>
                      <div class="flex gap-2">
                      <button
                          @click="prevPage"
                          :disabled="page === 1"
                          :class="[
                          'px-3 py-1 rounded border text-sm font-medium transition-colors',
                          page === 1
                              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          ]"
                      >
                          Previous
                      </button>
                      <button
                          @click="nextPage"
                          :disabled="page * pageSize >= totalRows"
                      :class="[
                          'px-3 py-1 rounded border text-sm font-medium transition-colors',
                          page * pageSize >= totalRows
                              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          ]"
                      >
                          Next
                      </button>
                      </div>
                  </div>
                  </div>
               </div>
           </div>
           <div
               v-if="showAddModal"
               class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
               @click.self="closeAddModal"
           >
               <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
               <h3 class="text-xl font-semibold text-gray-900 mb-4">Add New Entry</h3>
               <form @submit.prevent="saveEntry" class="space-y-4">
                   <div>
                   <label class="block text-sm font-medium text-gray-700 mb-1">
                       SKU <span class="text-red-500">*</span>
                   </label>
                   <select
                       v-model="newEntry.sku"
                       required
                       class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                       <option value="">Select SKU</option>
                       <option v-for="sku in products" :key="sku.sku" :value="sku.sku">
                       {{ sku.sku }} - {{ sku.name }}
                       </option>
                   </select>
                   </div>
       
                   <div>
                   <label class="block text-sm font-medium text-gray-700 mb-1">
                       Direction <span class="text-red-500">*</span>
                   </label>
                   <select
                       v-model="newEntry.reason"
                       required
                       class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                       <option value="">Select direction</option>
                       <option value="transfer_in">Transfer In (adds inventory)</option>
                       <option value="transfer_out">Transfer Out (removes inventory)</option>
                       <option value="adjustment">Adjustment (manual +/-)</option>
                   </select>
                   </div>
       
                   <div>
                   <label class="block text-sm font-medium text-gray-700 mb-1">
                       Quantity <span class="text-red-500">*</span>
                   </label>
                   <input
                       v-model.number="newEntry.qtyDelta"
                       type="number"
                       required
                       :min="reasonDirections[newEntry.reason] === 'manual' ? undefined : 1"
                       class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                       :placeholder="reasonDirections[newEntry.reason] === 'manual' ? 'Enter quantity (+ or -)' : 'Enter quantity'"
                   />
                   <p v-if="reasonDirectionHint" class="text-xs mt-1" :class="reasonDirections[newEntry.reason] === 'add' ? 'text-green-600' : reasonDirections[newEntry.reason] === 'subtract' ? 'text-red-600' : 'text-gray-500'">
                       {{ reasonDirectionHint }}
                   </p>
                   </div>
       
                   <div>
                   <label class="block text-sm font-medium text-gray-700 mb-1">
                       Explanation
                   </label>
                   <input
                       v-model="newEntry.referenceId"
                       type="text"
                       class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                       placeholder="Optional explanation"
                   />
                   </div>
       
                   <div class="flex gap-3 pt-2">
                   <button
                       type="button"
                       @click="closeAddModal"
                       class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                   >
                       Cancel
                   </button>
                   <button
                       type="submit"
                       class="flex-1 px-4 py-2 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold rounded-lg transition-colors"
                   >
                       Save Entry
                   </button>
                   </div>
               </form>
               </div>
           </div>

            <!-- Add Product Modal -->
            <div
                v-if="showAddProductModal"
                class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                @click.self="closeAddProductModal"
            >
                <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 class="text-xl font-semibold text-gray-900 mb-4">Add New Product</h3>
                <form @submit.prevent="saveProduct" class="space-y-4">
                    <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        SKU <span class="text-red-500">*</span>
                    </label>
                    <input
                        v-model="newProduct.sku"
                        type="text"
                        required
                        disabled
                        placeholder="Auto-generated from name"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    </div>
        
                    <div>
                     <label class="block text-sm font-medium text-gray-700 mb-1">
                         Name <span class="text-red-500">*</span>
                     </label>
                     <input
                         v-model="newProduct.name"
                         type="text"
                         required
                         placeholder="e.g. Sample Product Name"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                     </div>
         
                     <div>
                     <label class="block text-sm font-medium text-gray-700 mb-1">
                         Price ($)
                     </label>
                     <input
                         v-model.number="newProduct.price"
                         type="number"
                         step="0.01"
                         min="0"
                         placeholder="0.00"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                     </div>
        
                    <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                        Weight (oz)
                        </label>
                        <input
                        v-model.number="newProduct.weight"
                        type="number"
                        step="0.01"
                        placeholder="0.0"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                        Length (in)
                        </label>
                        <input
                        v-model.number="newProduct.length"
                        type="number"
                        step="0.01"
                        placeholder="0.0"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                        Width (in)
                        </label>
                        <input
                        v-model.number="newProduct.width"
                        type="number"
                        step="0.01"
                        placeholder="0.0"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                        Height (in)
                        </label>
                        <input
                        v-model.number="newProduct.height"
                        type="number"
                        step="0.01"
                        placeholder="0.0"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    </div>
        
                    <div class="flex gap-3 pt-2">
                    <button
                        type="button"
                        @click="closeAddProductModal"
                        class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="isSavingProduct"
                        class="flex-1 px-4 py-2 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                        {{ isSavingProduct ? 'Saving...' : 'Save Product' }}
                    </button>
                    </div>
                </form>
                </div>
            </div>

             <!-- Edit Product Modal -->
             <div
                 v-if="showEditProductModal"
                 class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                 @click.self="closeEditProductModal"
             >
                 <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                 <h3 class="text-xl font-semibold text-gray-900 mb-4">Edit Product</h3>
                 <form @submit.prevent="updateProductDetails" class="space-y-4">
                     <div>
                     <label class="block text-sm font-medium text-gray-700 mb-1">
                         SKU <span class="text-red-500">*</span>
                     </label>
                     <input
                         v-model="editingProduct.sku"
                         type="text"
                         required
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                     />
                     </div>
         
                     <div>
                     <label class="block text-sm font-medium text-gray-700 mb-1">
                         Name <span class="text-red-500">*</span>
                     </label>
                     <input
                         v-model="editingProduct.name"
                         type="text"
                         required
                         placeholder="e.g. Sample Product Name"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                     </div>
         
                     <div>
                     <label class="block text-sm font-medium text-gray-700 mb-1">
                         Price ($)
                     </label>
                     <input
                         v-model.number="editingProduct.price"
                         type="number"
                         step="0.01"
                         min="0"
                         placeholder="0.00"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                     </div>
         
                     <div class="grid grid-cols-2 gap-4">
                     <div>
                         <label class="block text-sm font-medium text-gray-700 mb-1">
                         Weight (oz)
                         </label>
                         <input
                         v-model.number="editingProduct.weight"
                         type="number"
                         step="0.01"
                         placeholder="0.0"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                     </div>
                     <div>
                         <label class="block text-sm font-medium text-gray-700 mb-1">
                         Length (in)
                         </label>
                         <input
                         v-model.number="editingProduct.length"
                         type="number"
                         step="0.01"
                         placeholder="0.0"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                     </div>
                     </div>

                     <div class="grid grid-cols-2 gap-4">
                     <div>
                         <label class="block text-sm font-medium text-gray-700 mb-1">
                         Width (in)
                         </label>
                         <input
                         v-model.number="editingProduct.width"
                         type="number"
                         step="0.01"
                         placeholder="0.0"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                     </div>
                     <div>
                         <label class="block text-sm font-medium text-gray-700 mb-1">
                         Height (in)
                         </label>
                         <input
                         v-model.number="editingProduct.height"
                         type="number"
                         step="0.01"
                         placeholder="0.0"
                         class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                     </div>
                     </div>
         
                     <div class="flex gap-3 pt-2">
                     <button
                         type="button"
                         @click="closeEditProductModal"
                         class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                     >
                         Cancel
                     </button>
                     <button
                         type="submit"
                         :disabled="isSavingEditedProduct"
                         class="flex-1 px-4 py-2 bg-[#83f6b8] hover:bg-[#6be4a5] text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
                     >
                         {{ isSavingEditedProduct ? 'Saving...' : 'Save Changes' }}
                     </button>
                     </div>
                 </form>
                 </div>
             </div>

            <div
               v-if="showHistoryModal"
               class="fixed inset-0 bg-black bg-opacity50 flex items-center justify-center p-4 z-50"
               @click.self="closeHistoryModal"
           >
               <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
               <div class="p-6 border-b border-gray-200">
                   <h3 class="text-xl font-semibold text-gray-900">Adjustment History</h3>
                   <p class="text-sm text-gray-600 mt-1">{{ selectedHistorySKU }}</p>
               </div>
               <div class="flex-1 overflow-y-auto p-6">
                   <div >
                   <div
                       v-for="(adj, idx) in adjustmentHistory"
                       :key="adj.id"
                       class="relative pl-8 pb-4"
                       :class="{ 'border-l-2 border-gray-200': idx < adjustmentHistory.length - 1 }"
                   >
                       <div
                       v-if="adjustmentHistory.length > 1"
                       :class="[
                           'absolute left-0 top-0 w-3 h-3 rounded-full border-2 border-white',
                           adj.qtyDelta > 0 ? 'bg-green-500' : 'bg-red-500'
                       ]"
                       style="left: -7px;"
                       ></div>
                       <div class="bg-gray-50 rounded-lg p-4">
                       <div class="flex items-start justify-between mb-2">
                           <div>
                           <span
                               :class="[
                               'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                               adj.qtyDelta > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                               ]"
                           >
                               {{ adj.qtyDelta > 0 ? '+' : '' }}{{ adj.qtyDelta }}
                           </span>
                           </div>
                           <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                           {{ adj.reason.replace('_', ' ') }}
                           </span>
                       </div>
                       <div class="text-xs text-gray-600 space-y-1">
                           <div><strong>Reference:</strong> {{ adj.referenceId }}</div>
                           <div class="flex items-center gap-2">
                            <strong>User:</strong> 
                                <div
                                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                                :style="{ backgroundColor: getAvatarColor(adj.user_id) }"
                                >
                                {{ getInitials(getUserById(adj.user_id).name) }}
                                </div>
                                {{ getUserById(adj.user_id).name }}
                            </div>
                           <div><strong>Date:</strong> {{ formatDate(adj.createdAt) }}</div>
                       </div>
                       </div>
                   </div>
                   </div>
               </div>
               <div class="p-4 border-t border-gray-200">
                   <button
                   @click="closeHistoryModal"
                   class="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                   >
                   Close
                   </button>
               </div>
               </div>
           </div>
           </div>
     </div>
   </div>
 </template>
 
 <script setup>
 import { ref, computed, watch,onMounted } from 'vue'
 import SideBar from '@/components/SideBar.vue';
 import { useStore } from 'vuex'
 import { fetchProducts, createProduct, updateProduct } from '@/api/labels'
 import Swal from 'sweetalert2'
 const store = useStore()
 const isDirecttide = computed(() => {
   return String(store.getters['auth/currentUser']?.organization ?? '').toLowerCase() === 'directtide'
 })
 // unwrap summary getter robustly
 const storeSummary = computed(() => {
   const raw = store.getters['inventory/summary']
   if (Array.isArray(raw)) return raw
   if (raw && Array.isArray(raw.summary)) return raw.summary
   if (raw && Array.isArray(raw.items)) return raw.items
   if (raw && Array.isArray(raw.data)) return raw.data
   return []
 })

 // unwrap ledger getter robustly
 const ledger = computed(() => {
    console.log('Ledger raw:', store.getters['inventory/ledger']) // Debug log
   const raw = store.getters['inventory/ledger']
   if (Array.isArray(raw)) return raw
   if (raw && Array.isArray(raw.entries)) return raw.entries
   return []
 })

 // unwrap totals.qtyDelta robustly
 const qtyDeltaTotal = computed(() => {
   const raw = store.getters['inventory/qtyDeltaTotal']
   if (typeof raw === 'number') return raw
   const totalsFromState = store.getters['inventory/totals']
   if (totalsFromState && typeof totalsFromState.qtyDelta === 'number') return totalsFromState.qtyDelta
   // try inside a ledger-shaped object
   const ledgerObj = store.getters['inventory/ledger']
   if (ledgerObj && ledgerObj.totals && typeof ledgerObj.totals.qtyDelta === 'number') {
     return ledgerObj.totals.qtyDelta
   }
   return 0
 })

 const page      = computed(() => store.getters['inventory/page'])
 const pageSize  = computed(() => store.getters['inventory/pageSize'])
 const totalRows = computed(() => store.getters['inventory/totalRows'])

 const reasonDirections = {
   adjustment: 'manual',
   transfer_in: 'add',
   transfer_out: 'subtract'
 }
 
 const products = ref([])
 

 const columns = [
   { key: 'sku', label: 'SKU' },
   { key: 'name', label: 'Name' },
   { key: 'qtyDelta', label: 'Qty Δ' },
   { key: 'reason', label: 'Reason' },
   { key: 'referenceId', label: 'Reference ID' },
   { key: 'user', label: 'User' },
   { key: 'createdAt', label: 'Created At' }
 ]
 
 const searchTerm = ref('')
 const reasonFilter = ref('')
 const sortKey = ref('createdAt')
 const sortOrder = ref('desc')
 const showAddModal = ref(false)
 const showHistoryModal = ref(false)
 const selectedHistorySKU = ref('')
 const inventorySearchTerm = ref('')
 const showLedger = ref(false)
 const showAddProductModal = ref(false)
 const isSavingProduct = ref(false)
 const newProduct = ref({
   sku: '',
   name: '',
   price: null,
   weight: null,
   length: null,
   width: null,
   height: null
 })
 
 const newEntry = ref({
   sku: '',
   qtyDelta: null,
   reason: '',
   referenceId: ''
 })
 
 function prevPage() {
   if (page.value > 1) store.dispatch('inventory/setPage', page.value - 1)
 }
 function nextPage() {
   const totalPages = Math.max(1, Math.ceil(totalRows.value / pageSize.value))
   if (page.value < totalPages) store.dispatch('inventory/setPage', page.value + 1)
 }

  const inventorySummary = computed(() => {
    const bySku = new Map(storeSummary.value.map(s => [s.sku, s]))
    return products.value.map(s => ({
      sku: s.sku,
      name: s.name,
      price: s.price,
      totalQty: Number(bySku.get(s.sku)?.totalQty ?? 0),
      adjustmentCount: Number(bySku.get(s.sku)?.adjustmentCount ?? 0)
    }))
  })

 const filteredInventorySummary = computed(() => {
   if (!inventorySearchTerm.value) return inventorySummary.value
   const term = inventorySearchTerm.value.toLowerCase()
   return inventorySummary.value.filter(item =>
     item.sku.toLowerCase().includes(term) ||
     item.name.toLowerCase().includes(term)
   )
 })
 
 const adjustmentHistory = computed(() =>
   store.getters['inventory/historyForSku'](selectedHistorySKU.value)
 )
 
 const reasonDirectionHint = computed(() => {
   if (!newEntry.value.reason) return ''
   const direction = reasonDirections[newEntry.value.reason]
   if (direction === 'add') return 'This will add to inventory (+)'
   if (direction === 'subtract') return 'This will subtract from inventory (-)'
   return 'Enter positive to add, negative to subtract'
 })
 
 function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }

  // 🔽 make the API call with the new sort (and reset to page 1 if you want)
  store.dispatch('inventory/fetchLedger', {
    page: 1,
    sortKey: sortKey.value,
    sortOrder: sortOrder.value
  })
}

 
 function formatDate(date) {
   return new Date(date).toLocaleString('en-US', {
     year: 'numeric',
     month: 'short',
     day: 'numeric',
     hour: '2-digit',
     minute: '2-digit'
   })
 }
 
 function openAddModal() {
   showAddModal.value = true
 }
 
 function closeAddModal() {
   showAddModal.value = false
   newEntry.value = {
     sku: '',
     qtyDelta: null,
     reason: '',
     referenceId: ''
   }
 }

  function openAddProductModal() {
    showAddProductModal.value = true
  }

  function closeAddProductModal() {
    showAddProductModal.value = false
    newProduct.value = {
      sku: '',
      name: '',
      price: null,
      weight: null,
      length: null,
      width: null,
      height: null
    }
  }

  async function saveProduct() {
    if (!newProduct.value.sku || !newProduct.value.name) return
    isSavingProduct.value = true
    try {
      const payload = {
        sku: newProduct.value.sku,
        name: newProduct.value.name
      }
      if (newProduct.value.price != null && newProduct.value.price !== '') payload.price = newProduct.value.price
      if (newProduct.value.weight != null && newProduct.value.weight !== '') payload.weight = newProduct.value.weight
      if (newProduct.value.length != null && newProduct.value.length !== '') payload.length = newProduct.value.length
      if (newProduct.value.width != null && newProduct.value.width !== '') payload.width = newProduct.value.width
      if (newProduct.value.height != null && newProduct.value.height !== '') payload.height = newProduct.value.height

      await createProduct(payload)
      
      // Refresh products list
      products.value = await fetchProducts()
      
      Swal.fire({
        icon: 'success',
        title: 'Product Created',
        text: `Product ${payload.name} was successfully created.`,
        timer: 2000,
        showConfirmButton: false
      })
      closeAddProductModal()
    } catch (err) {
      console.error('Failed to create product:', err)
      Swal.fire({
        icon: 'error',
        title: 'Failed to Create Product',
        text: err.response?.data?.error || err.message || 'Unknown error occurred.'
      })
    } finally {
      isSavingProduct.value = false
    }
  }
 
 async function saveEntry() {
   const selectedSKU = products.value.find(s => s.sku === newEntry.value.sku)
   if (!selectedSKU) return

   let finalQty = Math.abs(newEntry.value.qtyDelta)
   const direction = reasonDirections[newEntry.value.reason]

   if (direction === 'subtract') finalQty = -finalQty
   else if (direction === 'manual') finalQty = newEntry.value.qtyDelta

   const payload = {
     sku: newEntry.value.sku,
     name: selectedSKU.name,
     qtyDelta: finalQty,
     reason: newEntry.value.reason,
     referenceId: newEntry.value.referenceId || null
   }

   try {
     await store.dispatch('inventory/createEntry', payload)
     await store.dispatch('inventory/fetchLedger')
     await store.dispatch('inventory/fetchSummary')
   } catch (err) {
     console.error('Failed to save entry:', err)
   } finally {
     closeAddModal()
   }
 }
 
 async function openAdjustmentHistory(sku) {
   selectedHistorySKU.value = sku
   await store.dispatch('inventory/fetchHistory', sku)
   showHistoryModal.value = true
 }
 
  function closeHistoryModal() {
    showHistoryModal.value = false
    selectedHistorySKU.value = ''
  }

  const showEditProductModal = ref(false)
  const isSavingEditedProduct = ref(false)
  const editingProduct = ref({
    id: null,
    sku: '',
    name: '',
    price: null,
    weight: null,
    length: null,
    width: null,
    height: null
  })

  function openEditProductModal(item) {
    const prod = products.value.find(p => p.sku === item.sku)
    if (prod) {
      editingProduct.value = {
        id: prod.id,
        sku: prod.sku,
        name: prod.name,
        price: prod.price,
        weight: prod.weight,
        length: prod.length,
        width: prod.width,
        height: prod.height
      }
      showEditProductModal.value = true
    }
  }

  function closeEditProductModal() {
    showEditProductModal.value = false
    editingProduct.value = {
      id: null,
      sku: '',
      name: '',
      price: null,
      weight: null,
      length: null,
      width: null,
      height: null
    }
  }

  async function updateProductDetails() {
    if (!editingProduct.value.id || !editingProduct.value.sku || !editingProduct.value.name) return
    isSavingEditedProduct.value = true
    try {
      const payload = {
        sku: editingProduct.value.sku,
        name: editingProduct.value.name,
        price: editingProduct.value.price !== undefined && editingProduct.value.price !== '' ? editingProduct.value.price : 0.00,
        weight: editingProduct.value.weight !== undefined && editingProduct.value.weight !== '' ? editingProduct.value.weight : null,
        length: editingProduct.value.length !== undefined && editingProduct.value.length !== '' ? editingProduct.value.length : null,
        width: editingProduct.value.width !== undefined && editingProduct.value.width !== '' ? editingProduct.value.width : null,
        height: editingProduct.value.height !== undefined && editingProduct.value.height !== '' ? editingProduct.value.height : null
      }

      await updateProduct(editingProduct.value.id, payload)

      products.value = await fetchProducts()
      await store.dispatch('inventory/fetchSummary')

      Swal.fire({
        icon: 'success',
        title: 'Product Updated',
        text: `Product ${payload.name} was successfully updated.`,
        timer: 2000,
        showConfirmButton: false
      })
      closeEditProductModal()
    } catch (err) {
      console.error('Failed to update product:', err)
      Swal.fire({
        icon: 'error',
        title: 'Failed to Update Product',
        text: err.response?.data?.error || err.message || 'Unknown error occurred.'
      })
    } finally {
      isSavingEditedProduct.value = false
    }
  }
  watch([searchTerm, reasonFilter], () => {
    store.dispatch('inventory/setFilters', {
      search: searchTerm.value,
      reason: reasonFilter.value
    })
  })

  watch(() => newProduct.value.name, (newName) => {
    if (newName) {
      newProduct.value.sku = newName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
    } else {
      newProduct.value.sku = ''
    }
  })

  watch(isDirecttide, (val) => {
    if (val) showLedger.value = true
  }, { immediate: true })

 const users = computed(() =>
  store.getters['leads/allUsers'] ??
  store.getters['allUsers'] ??
  store.state.leads?.users ?? []
)
 

 onMounted(async () => {
   try {
     await store.dispatch('inventory/fetchSummary')
     await store.dispatch('inventory/fetchLedger')
     await store.dispatch('leads/fetchUserMap')  
     products.value = await fetchProducts()

   } catch (e) {
     console.error('Failed to load inventory summary:', e)
   }
 })

 const getUserById = id => users.value.find(u => u.id == id) || {}
 const getAvatarColor = id => {
  const palette = ['#FFB3BA','#FFDFBA','#FFFFBA','#BAFFC9','#BAE1FF','#E5BAFF','#D5F4E6','#FFCCE5','#C5FAD5','#FFF2CC']
  return palette[id % palette.length]
}
 const getInitials = name => (name || '').split(' ').map(p => p[0]).slice(0,2).join('').toUpperCase()

 </script>
